import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus, ShieldCheck, ShoppingBag, Sprout, Store } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth, errCode } from "../context/AuthContext";
import { useSecurity } from "../hooks/useSecurity";
import GoogleButton from "../components/GoogleButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [["buyer", ShoppingBag], ["farmer", Sprout], ["trader", Store]];

export default function UnifiedAuthPage() {
  const { t } = useApp();
  const { user, saveSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const initialType = TYPES.some(([id]) => id === params.get("type")) ? params.get("type") : "buyer";
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [type, setType] = useState(initialType);
  const [busy, setBusy] = useState(false);
  const { getProof, reset, ready, Honeypot } = useSecurity();
  const [form, setForm] = useState({ name: "", email: "", password: "", farm_name: "", region: "", trader_name: "" });

  if (user) return <Navigate to={next} replace />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const security = await getProof();
      const payload = mode === "register" ? { ...form, account_type: type, security } : { email: form.email, password: form.password, security };
      const { data } = await axios.post(`${API}/auth/${mode}`, payload);
      saveSession(data);
      navigate(next);
    } catch (err) {
      toast.error(err?.response?.status === 422 ? t("err.VALIDATION") : t(`err.${errCode(err)}`));
      reset();
    } finally { setBusy(false); }
  };

  return (
    <main className="auth-page" data-testid="auth-page">
      <div className="auth-card">
        <div className="auth-tabs">
          <button className={`auth-tab ${mode === "login" ? "active" : ""}`} onClick={() => setMode("login")} data-testid="auth-tab-login"><LogIn size={14} /> {t("auth.tab_login")}</button>
          <button className={`auth-tab ${mode === "register" ? "active" : ""}`} onClick={() => setMode("register")} data-testid="auth-tab-register"><UserPlus size={14} /> {t("auth.tab_register")}</button>
        </div>
        <h1 className="auth-title" data-testid="auth-title">{mode === "login" ? t("auth.login_title") : t("ua.register_title")}</h1>
        <p className="admin-muted auth-sub">{t("ua.subtitle")}</p>

        <form onSubmit={submit} className="auth-form">
          {mode === "register" && (
            <>
              <div className="ua-types" role="radiogroup" data-testid="account-type-picker">
                {TYPES.map(([id, Icon]) => (
                  <button type="button" key={id} role="radio" aria-checked={type === id} className={`ua-type ${type === id ? "active" : ""}`} onClick={() => setType(id)} data-testid={`account-type-${id}`}>
                    <Icon size={16} /><strong>{t(`ua.type.${id}`)}</strong><small>{t(`ua.type.${id}.hint`)}</small>
                  </button>
                ))}
              </div>
              <div className="p3-field"><label className="p3-label">{t("auth.name")}</label>
                <input className="p3-input" required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name-input" /></div>
              {type === "farmer" && (
                <>
                  <div className="p3-field"><label className="p3-label">{t("auth.farm")}</label>
                    <input className="p3-input" required minLength={2} value={form.farm_name} onChange={(e) => set("farm_name", e.target.value)} data-testid="register-farm-input" /></div>
                  <div className="p3-field"><label className="p3-label">{t("auth.region")}</label>
                    <input className="p3-input" required minLength={2} value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="register-region-input" /></div>
                </>
              )}
              {type === "trader" && (
                <div className="p3-field"><label className="p3-label">{t("ua.traderName")}</label>
                  <input className="p3-input" minLength={2} value={form.trader_name} onChange={(e) => set("trader_name", e.target.value)} data-testid="register-trader-input" /></div>
              )}
            </>
          )}
          <div className="p3-field"><label className="p3-label">{t("auth.email")}</label>
            <input type="email" className="p3-input" required placeholder="name@example.com" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="auth-email-input" /></div>
          <div className="p3-field"><label className="p3-label">{t("auth.password")}</label>
            <input type="password" className="p3-input" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="auth-password-input" /></div>
          <Honeypot />
          <button type="submit" className="btn-lux-gold auth-submit" disabled={busy} data-testid="auth-submit-button">
            {busy ? <Loader2 size={16} className="spin" /> : mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
            {busy ? t("auth.working") : mode === "login" ? t("auth.submit_login") : t("auth.submit_register")}
          </button>
          <p className={`sec-note ${ready ? "ok" : ""}`} data-testid="security-note"><ShieldCheck size={12} /> {t("sec.note")}</p>
        </form>
        <GoogleButton portal={mode === "register" ? type : "farmer"} testId="google-login-button" />
      </div>
    </main>
  );
}
